import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HugeiconsIconComponent } from '@hugeicons/angular';

export type ButtonVariant = 'primary' | 'secondary' | 'secondaryMuted' | 'outline' | 'ghost' | 'danger' | 'dangerOutline' | 'success' | 'dangersoft' | 'selectfile' | 'outlineLight' | 'btnlink';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
    selector: 'app-button',
    standalone: true,
    imports: [CommonModule, HugeiconsIconComponent],
    templateUrl: './button.html',
    styleUrl: './button.scss',
})
export class Button {
    // Inputs
    nowrap = input<boolean>(false);
    label = input<string>('');
    variant = input<ButtonVariant>('primary');
    size = input<ButtonSize>('md');
    disabled = input<boolean>(false);
    loading = input<boolean>(false);
    type = input<'button' | 'submit' | 'reset'>('button');
    icon = input<any | null>(null);
    iconPosition = input<'left' | 'right' | 'center'>('left');
    /** Appended to the inner button classes after variant/size. Use `!` utilities when overriding variant colors (same CSS specificity). */
    extraClass = input<string>('');

    // Outputs
    clicked = output<void>();

    // Computed classes
    buttonClasses = computed(() => {
        const base = 'inline-flex cursor-pointer items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';

        const variants = {
            primary: 'bg-primary border-2 border-primary text-white hover:bg-primary/90 focus:ring-primary',
            secondary: 'bg-secondary text-white border-2 border-secondary hover:bg-secondary/90 focus:ring-secondary',
            secondaryMuted: 'bg-secondary-muted border-2 border-secondary-muted text-secondary hover:bg-secondary-muted/90 focus:ring-secondary-muted',
            outline: 'border-2 border-primary text-primary hover:bg-primary-soft focus:ring-primary',
            outlineLight: 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-300',
            ghost: 'text-text-strong hover:bg-surface focus:ring-primary',
            danger: 'bg-error border-2 border-error text-white hover:bg-error/90 focus:ring-error',
            dangerOutline: 'border-2 border-error text-error bg-transparent hover:bg-error-soft focus:ring-error',
            dangersoft: 'bg-error-soft border-2 border-error-soft text-error hover:bg-error-soft/90 focus:ring-error',
            success: 'bg-success border-2 border-success text-white hover:bg-success/90 focus:ring-success',
            selectfile: 'bg-bg text-muted border border-border-subtle hover:bg-bg/90 focus:ring-bg min-w-[200px] justify-start',
            btnlink:'border-0 focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-0 focus:shadow-0'
        };

        const sizes = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2 text-base',
            lg: 'px-6 py-3 text-lg',
        };

        const extra = this.extraClass().trim();
        return `${base} ${variants[this.variant()]} ${sizes[this.size()]} ${this.nowrap() ? 'whitespace-nowrap' : ''}${extra ? ` ${extra}` : ''}`;
    });

    onClick(): void {
        if (!this.disabled() && !this.loading()) {
            this.clicked.emit();
        }
    }
}
